<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    caption?: string;
    description?: string;
    /**
     * `embedded` is the table INSIDE a detail card (spec §8, the Person page's
     * Beschäftigung block): no frame, no shadow, and a column row that scrolls
     * with the card instead of pinning — the card is the frame, so it owns no
     * scroll frame or shadows of its own either.
     */
    variant?: 'shadow' | 'border' | 'embedded';
    /**
     * Opt the table into being its own scroller: the frame caps at
     * `--lumi-table-max-h` (override it on the instance), scrolls its rows inside
     * that cap and pins the column row to the top of THIS frame. Off by default —
     * the table then grows with its rows and the page is the only vertical
     * scroller. Meant for a page whose body is the table and nothing else (a
     * toolbar row above, at most a pager below). `embedded` ignores it.
     */
    fill?: boolean;
    /**
     * The "Mehr laden" footer (spec §8): shown while a further page exists. The
     * label comes from the host app (component-lib carries no i18n of its own).
     */
    hasMore?: boolean;
    loadMoreLabel?: string;
    /** The load-more button spins and is inert while the next page is on its way. */
    loadingMore?: boolean;
  }>(),
  { variant: 'shadow', fill: false, hasMore: false, loadMoreLabel: 'Mehr laden', loadingMore: false },
);

defineEmits<{ 'load-more': [] }>();

// Two more instance variables, same contract: `--lumi-table-layout` (`auto` lets columns
// follow their content — a reference table whose text must fold; an embedded table is
// always `auto`) and `--lumi-table-edge` (the outer-cell gutter of an embedded
// table whose host card does not pad; default 0).
//
// By default the table grows with its rows: the PAGE is the only vertical scroller and
// the column row scrolls away with it (only a wide table scrolls sideways, inside the
// frame). `fill` opts into the capped frame instead: it caps at --lumi-table-max-h (a
// consumer overrides that var on the instance, e.g. `style="--lumi-table-max-h: …"`),
// scrolls its own rows past the cap and pins the column row inside that frame (#471) — a
// short table just stays content-height, nothing forces it open. `embedded` never caps:
// it scrolls with its host card, so it gets neither the cap nor the scroll shadows.
const bounded = computed(() => props.fill && props.variant !== 'embedded');
const scrollEl = ref<HTMLElement | null>(null);
const tableEl = ref<HTMLElement | null>(null);
const moreAbove = ref(false);
const moreBelow = ref(false);
// Sideways (#1105): a table wider than its frame scrolls sideways and pins its trailing
// action column (`.lumi-table__end`) to the frame's right edge, so a row's actions
// never scroll out of reach; the pinned column casts a shade while columns lie under it.
// Both are measured, never assumed: a table that fits changes nothing at all.
const scrollsX = ref(false);
const moreRight = ref(false);
// Two widths the CSS cannot know (#1105 follow-up). The trailing action column: its
// header is usually a visually hidden label, so under fixed layout the column would be
// only as wide as that header (its padding) and the row's buttons would spill past it —
// that spill alone made a table that fits scroll sideways. It is measured from the
// widest action cell's content. And the frame's visible width, so a full-width row
// (`.lumi-table__frame-span`, e.g. an expanded detail) stays put while columns scroll.
const endWidth = ref(0);
const frameWidth = ref(0);
const sizeVars = computed(() => ({
  ...(endWidth.value ? { '--lumi-table-end-w': `${endWidth.value}px` } : {}),
  ...(frameWidth.value ? { '--lumi-table-frame-w': `${frameWidth.value}px` } : {}),
}));

function measureEnd() {
  const table = tableEl.value;
  if (!table || typeof document === 'undefined' || !document.createRange) return;
  let widest = 0;
  for (const cell of table.querySelectorAll<HTMLElement>('td.lumi-table__end')) {
    const range = document.createRange();
    range.selectNodeContents(cell);
    const content = range.getBoundingClientRect().width;
    if (!content) continue;
    const style = getComputedStyle(cell);
    widest = Math.max(widest, content + parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0'));
  }
  if (widest) endWidth.value = Math.ceil(widest);
}

function measure() {
  const el = scrollEl.value;
  if (!el) return;
  if (el.clientWidth) frameWidth.value = el.clientWidth;
  const overflowX = el.scrollWidth - el.clientWidth;
  scrollsX.value = overflowX > 1;
  moreRight.value = overflowX - Math.abs(el.scrollLeft) > 1;
  if (!bounded.value) return;
  moreAbove.value = el.scrollTop > 1;
  moreBelow.value = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
}

let observer: ResizeObserver | null = null;
function measureAll() {
  measureEnd();
  measure();
}

onMounted(() => {
  measureAll();
  // Rows can arrive/leave without a scroll event (a filter, a page load); a test
  // environment without ResizeObserver just keeps the initial measurement.
  if (typeof ResizeObserver !== 'undefined' && scrollEl.value) {
    observer = new ResizeObserver(measureAll);
    observer.observe(scrollEl.value);
    // The frame keeps its width when only the content widens (columns appear, rows load).
    if (tableEl.value) observer.observe(tableEl.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <div
    class="lumi-table"
    :class="[`lumi-table--${variant}`, { 'lumi-table--fill': bounded, 'lumi-table--scroll-x': scrollsX, 'lumi-table--more-right': scrollsX && moreRight }]"
    :style="sizeVars"
  >
    <div v-if="caption || $slots.toolbar" class="lumi-table__head">
      <div class="lumi-table__heading">
        <h3 v-if="caption" class="lumi-table__caption">{{ caption }}</h3>
        <p v-if="description" class="lumi-table__desc">{{ description }}</p>
      </div>
      <div v-if="$slots.toolbar" class="lumi-table__toolbar"><slot name="toolbar" /></div>
    </div>
    <!-- `__body` is a plain, non-scrolling positioned box that hugs `__scroll`'s
         rendered height exactly (its only child). The fades are ITS children,
         not `__scroll`'s: an absolutely positioned child of a scroll container
         is part of that container's scrollable content and moves with it, so
         positioning them here instead — against a box that never scrolls — is
         what actually keeps them pinned to the frame while rows scroll under
         them. `top:40px` (matching the header's own height) lands right under
         the header regardless of whatever optional content sits above `__body`
         (a caption/toolbar row), since `__body` starts exactly where `__scroll`
         starts either way. -->
    <div class="lumi-table__body">
      <div ref="scrollEl" class="lumi-table__scroll" tabindex="0" @scroll.passive="measure">
        <table ref="tableEl" class="lumi-table__table">
          <slot />
        </table>
      </div>
      <div v-if="bounded && moreAbove" class="lumi-table__fade lumi-table__fade--top" aria-hidden="true" />
      <div v-if="bounded && moreBelow" class="lumi-table__fade lumi-table__fade--bottom" aria-hidden="true" />
    </div>
    <div v-if="$slots.footer || hasMore" class="lumi-table__foot">
      <div v-if="$slots.footer" class="lumi-table__foot-info"><slot name="footer" /></div>
      <button
        v-if="hasMore"
        type="button"
        class="lumi-table__more"
        :disabled="loadingMore"
        :aria-busy="loadingMore"
        @click="$emit('load-more')"
      >
        <span v-if="loadingMore" class="lumi-table__more-spinner" aria-hidden="true" />
        {{ loadMoreLabel }}
      </button>
    </div>
  </div>
</template>

<style>
/* The framed table (spec §8, Personen): white on the card border, 12 px radius,
   uppercase 11 px tertiary column labels, 50 px rows, tabular numbers.

   By default the page is the only vertical scroller: `.lumi-table__scroll` grows
   with the rows and scrolls sideways only (a fixed-layout table can overflow in
   total column width), and the column row is NOT sticky — inside a frame that
   does not scroll vertically it would pin to the page scroller. `fill` (the
   `--fill` modifier) makes the frame the scroll container instead (#471): it caps
   at `--lumi-table-max-h`, scrolls its own rows and pins the column row at
   `top:0` of THAT box. `overflow: clip` on the outer frame keeps the corners
   round without itself becoming a second scroll container. `embedded` (the table
   inside a detail card) never caps — the card is its frame, so it is unbounded,
   unshadowed and scrolls with the card. */
.lumi-table{--lumi-table-end-w:max-content;--lumi-table-frame-w:100%;position:relative;background:var(--surface-card);border-radius:var(--radius-lg);overflow:clip;font-family:var(--font-body);}
.lumi-table--shadow{box-shadow:var(--shadow-xs);border:1px solid var(--border-default);}
.lumi-table--border{border:1px solid var(--border-default);}
.lumi-table--embedded{background:transparent;border-radius:0;}
/* Every column keeps at least --space-2 a side so neighbouring figures and labels
   never run into each other. The first and last cell drop their OUTER padding
   because the embedded table assumes a host card that pads (the Person page's
   detail cards): the card's own padding is then the gutter. A host that does not
   pad (a full-bleed card, so the row rules run edge to edge) sets `--lumi-table-edge`
   on the table (Zeit's `.zeit-edge-table`) — the same way `--lumi-table-max-h` is set. */
.lumi-table--embedded .lumi-table__table th{position:static;background:transparent;height:32px;padding-inline:var(--space-2);}
.lumi-table--embedded .lumi-table__table td{padding-inline:var(--space-2);height:var(--row-h-sm);}
.lumi-table--embedded .lumi-table__table :is(th,td):first-child{padding-inline-start:var(--lumi-table-edge);}
.lumi-table--embedded .lumi-table__table :is(th,td):last-child{padding-inline-end:var(--lumi-table-edge);}
.lumi-table--embedded .lumi-table__table tbody tr:hover td{background:transparent;}
.lumi-table__head{display:flex;align-items:center;justify-content:space-between;gap:var(--space-3);padding:var(--space-3) var(--space-4);border-bottom:1px solid var(--border-default);}
.lumi-table__caption{font-size:var(--text-sm);font-weight:var(--weight-bold);letter-spacing:var(--tracking-display);color:var(--text-heading);margin:0;}
.lumi-table__desc{font-size:var(--text-xs);color:var(--text-secondary);margin:2px 0 0;}
.lumi-table__toolbar{display:flex;align-items:center;gap:var(--space-2);flex-shrink:0;}
.lumi-table__body{position:relative;}
.lumi-table__scroll{overflow-x:auto;}
/* Small viewports (#1105): below `--lumi-table-min-w` a framed table stops squeezing
   its columns and scrolls sideways inside the frame instead — the page never scrolls
   sideways. A table whose columns need more room than the default raises it on the
   instance (`style="--lumi-table-min-w: 66rem"`). The embedded table lays out by
   content (below), so it never needs one. */
.lumi-table__table{min-width:var(--lumi-table-min-w);}
.lumi-table--embedded .lumi-table__table{min-width:0;}
.lumi-table--fill .lumi-table__scroll{overflow:auto;max-height:var(--lumi-table-max-h,var(--table-max-h));}
.lumi-table--embedded .lumi-table__scroll{overflow-x:auto;scrollbar-width:thin;}
/* Fixed layout cannot shrink-wrap: with no <colgroup>, `width:1%` on the
   action column is taken literally and every other column splits the rest
   equally — the trailing "Edit" ends up clipped at the card's edge. The card
   is the embedded table's frame and its rows are short, so it lays out by
   content instead; the action column then sizes to its buttons. A card too
   narrow even for that (the two-column person page on a laptop) scrolls the
   table sideways rather than hiding its actions — hence `overflow-x` above. */
.lumi-table--embedded .lumi-table__table{table-layout:auto;}
/* Auto layout sizes columns by content, so a body cell may wrap where the
   framed table would ellipsise: in a narrow card the text folds instead of
   the action column being pushed out of the card. The action cell keeps its
   buttons on one line (it decides its own wrapping, if any). */
.lumi-table--embedded .lumi-table__table td:not(.lumi-table__end){white-space:normal;}
/* The frame clips outward outlines (`overflow:clip`), so the focus ring — set
   globally on every focusable element (tokens/base.css) — draws inward here. */
.lumi-table__scroll:focus-visible{outline-offset:-2px;}
.lumi-table__table{width:100%;border-collapse:separate;border-spacing:0;font-size:var(--text-sm);table-layout:var(--lumi-table-layout);font-variant-numeric:tabular-nums;}
/* The column row scrolls with the page by default. Under `fill` it is pinned to the
   top of the table's OWN scroll frame, not the page (#471) — separate borders (not
   collapse) so the pinned row keeps its bottom rule while it floats. */
.lumi-table__table th{background:var(--surface-card);text-align:left;height:40px;font-size:var(--text-caps);font-weight:var(--weight-semibold);letter-spacing:var(--tracking-caps);text-transform:uppercase;color:var(--text-tertiary);padding:0 var(--space-4);box-shadow:inset 0 -1px 0 var(--border-default);white-space:nowrap;vertical-align:middle;overflow:hidden;text-overflow:ellipsis;}
.lumi-table--fill .lumi-table__table th{position:sticky;top:0;z-index:var(--layer-raised);}
.lumi-table__table td{height:var(--row-h);padding:0 var(--space-4);border-bottom:1px solid var(--border-default);color:var(--text-body);vertical-align:middle;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
/* Scroll cues (#471): a shade right below the header line while rows are
   scrolled out of view above it, one along the bottom while more follow below.
   40px matches the header row's own height, above. Positioned against `__body`
   (see the template), not `__scroll` — see that comment for why. */
.lumi-table__fade{position:absolute;left:0;right:0;height:var(--fade-h);pointer-events:none;}
.lumi-table__fade--top{top:40px;background:linear-gradient(to bottom,var(--surface-card),transparent);}
.lumi-table__fade--bottom{bottom:0;background:linear-gradient(to top,var(--surface-card),transparent);}
/* Shrink-wrap trailing action cells so buttons stay fully visible. `width:1%`
   does NOT do that under `table-layout:fixed` with no <colgroup> (issue 563):
   fixed layout takes a percentage/pixel width literally regardless of content,
   so the column really does render at ~1% of the table's width — too narrow
   for the header label and the row's icon buttons, which then either clip
   (`overflow:hidden`) or, with `overflow:visible`, paint past the column's own
   box. That painted-past content still falls inside `.lumi-table__scroll`
   (`overflow:auto`), so the scroll container's scrollable width grows by
   exactly the spillover and the table gains a horizontal scrollbar it does
   not need, with the row's bottom border stopping at the column's real (tiny)
   edge instead of under the icons. `width:max-content` sizes the column to
   its own widest cell across EVERY row (header included), which is exactly
   what "shrink-wrap" was after; a consumer that supplies its own <colgroup>
   width or inline `style="width:…"` for this column still wins over this
   class (a `<col>` width, and inline styles, both outrank it), so this only
   changes the tables that had nothing else sizing the column. */
.lumi-table__table th.lumi-table__end,
.lumi-table__table td.lumi-table__end{text-align:right;width:max-content;overflow:visible;}
/* …except that a header holding only a visually hidden label has no max-content to speak
   of, so the column still came out at its padding. Once measured, the widest action
   cell's content sizes the column (#1105 follow-up); max-content stays the fallback. */
.lumi-table__table th.lumi-table__end{width:var(--lumi-table-end-w,max-content);}
/* A full-width row (one cell spanning every column: an expanded detail panel) wraps its
   content in `.lumi-table__frame-span` so it stays pinned to the frame's visible width
   while the columns scroll sideways under it, instead of scrolling out with them. The
   wrapper carries the cell's padding; give its cell `padding:0`. Its cell must not clip
   (a cell's `overflow:hidden` would make IT the sticky scroller, pinning to nothing). */
.lumi-table__table td:has(> .lumi-table__frame-span){overflow:visible;}
.lumi-table__frame-span{position:sticky;left:0;box-sizing:border-box;width:var(--lumi-table-frame-w,100%);max-width:100%;}
/* While the table scrolls sideways its trailing action column stays pinned to the
   frame's right edge (#1105), opaque so the columns sliding under it do not show
   through, and following the row's hover/selection fill. The shade on its left edge
   shows only while columns lie under it (scrolled fully right, it goes). */
.lumi-table--scroll-x .lumi-table__table :is(th,td).lumi-table__end{position:sticky;right:0;z-index:var(--layer-raised);background:var(--surface-card);}
.lumi-table--scroll-x.lumi-table--fill .lumi-table__table th.lumi-table__end{z-index:calc(var(--layer-raised) + 1);}
.lumi-table--scroll-x:not(.lumi-table--embedded) .lumi-table__table tbody tr:hover td.lumi-table__end{background:var(--surface-wash);}
.lumi-table--scroll-x .lumi-table__table tbody tr[aria-selected="true"] td.lumi-table__end{background:var(--selected-bg);}
.lumi-table--more-right .lumi-table__table td.lumi-table__end{box-shadow:var(--shadow-pin-edge);}
.lumi-table--more-right .lumi-table__table th.lumi-table__end{box-shadow:inset 0 -1px 0 var(--border-default),var(--shadow-pin-edge);}
.lumi-table__table tbody tr:last-child td{border-bottom:none;}
.lumi-table__table tbody tr:hover td{background:var(--surface-wash);}
/* A selected row (a consumer marks it with aria-selected) takes the lavender selection. */
.lumi-table__table tbody tr[aria-selected="true"] td{background:var(--selected-bg);}
.lumi-table__foot{display:flex;align-items:center;justify-content:center;gap:var(--space-3);padding:var(--space-3) var(--space-4);border-top:1px solid var(--border-default);font-size:var(--text-xs);color:var(--text-secondary);}
.lumi-table__foot-info{flex:1;min-width:0;}
.lumi-table__foot:has(.lumi-table__foot-info) .lumi-table__more{margin-left:auto;}
.lumi-table__more{display:inline-flex;align-items:center;gap:8px;height:var(--control-h-sm);padding:0 12px;border:1px solid var(--panel-edge);border-radius:var(--radius-md);background:var(--surface-card);color:var(--text-heading);font:inherit;font-size:var(--text-sm);font-weight:var(--weight-medium);cursor:pointer;transition:background-color var(--dur-fast);}
.lumi-table__more:hover:not(:disabled){background:var(--surface-wash);}
.lumi-table__more:disabled{opacity:.6;cursor:progress;}
.lumi-table__more-spinner{width:14px;height:14px;border-radius:50%;border:2px solid var(--pill-ring);border-top-color:var(--btn-primary-bg);animation:lumi-spin 0.6s linear infinite;}
</style>
